import {GoogleMap, Marker, OverlayView, Polyline} from '@react-google-maps/api'
import React, {useEffect, useState} from 'react'
import {LatLng,} from 'use-places-autocomplete'
import {CheckPoint, FinishLine} from '../../shared/types/training.type'
import {PolylinePosition} from './FlCpMap'
import {finishLine64} from '../../assets/icons/finishLineBase64'
import {chunk, get, isEmpty} from 'lodash'
import {Typography, useMediaQuery, useTheme} from '@mui/material'

type MapProps = {
    center?: LatLng,
    finishline?: FinishLine,
    checkPoints?: CheckPoint[],
    currentIndex: number,
    latLngs: (google.maps.LatLngLiteral & { color?: string })[]
    totalBound?: google.maps.LatLngBounds
}


function MapV2({totalBound, latLngs, center, currentIndex, checkPoints, finishline}: MapProps) {
    const [map, setMap] = React.useState<google.maps.Map | null>(null)
    const [initialShow, setInitialShow] = useState(true)
    const [flagIcon, setFlagIcon] = React.useState<google.maps.Icon | null>(null);

    //Polylines and infos
    const [polylineFl, setPolylineFl] = React.useState<PolylinePosition[]>([]);
    const [polylinesCp, setPolylinesCp] = React.useState<PolylinePosition[][]>([]);
    const [flagFl, setFlagFl] = React.useState<PolylinePosition | null>(null);
    const [cpsLabels, setCpsLabels] = React.useState<{ text: string, pos: PolylinePosition }[]>([])


    const theme = useTheme()
    const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

    useEffect(() => {
        handleFinishLine()
        handleCheckPoints()
        if (initialShow) {
            setInitialShow(false)
        }
    }, [currentIndex, finishline?.index_moment, checkPoints?.length, initialShow])


    useEffect(() => {
        if (map && totalBound) {
            map.fitBounds(totalBound)
        }
    }, [totalBound, map])

    //Fns


    const getMidpoint = (path: PolylinePosition[]): PolylinePosition => ({
        lat: (path[0].lat + path[1].lat) / 2 + 0.0004,
        lng: (path[0].lng + path[1].lng) / 2 - 0.0002,
    });


    const handleFinishLine = () => {
        if (!finishline?.finish_line || !finishline?.index_moment) {
            setPolylineFl([])
            setFlagFl(null)
            return
        }

        console.log('entered');
        console.log({finishline, currentIndex});

        //draw finish line if current Index > fl index moment
        if ((currentIndex >= finishline.index_moment) || currentIndex === 0) {
            let markers: PolylinePosition[] = [{
                lat: finishline.finish_line.latitude_a,
                lng: finishline.finish_line.longitude_a
            }, {lat: finishline.finish_line.latitude_b, lng: finishline.finish_line.longitude_b}]


            setPolylineFl(markers)
            setFlagFl(markers[0])
        } else {
            setPolylineFl([])
            setFlagFl(null)
        }
    }

    const handleCheckPoints = () => {
        if (!checkPoints || isEmpty(checkPoints)) return
        //draw finish line if current Index > fl index moment

        let filterCheckpointsByCurrentIndex = (currentIndex === 0 && initialShow) ? checkPoints : checkPoints.filter(cp => currentIndex >= cp.index_moment)

        if (isEmpty(filterCheckpointsByCurrentIndex)) {
            setPolylinesCp([])
            setCpsLabels([])
            return
        }

        let markers: typeof polylinesCp = []
        let labels: typeof cpsLabels = []
        filterCheckpointsByCurrentIndex.forEach(cp => {

            let position: PolylinePosition[] = [{
                lat: cp.checkpoint.latitude_a ?? 0,
                lng: cp.checkpoint.longitude_a ?? 0
            }, {lat: cp.checkpoint.latitude_b ?? 0, lng: cp.checkpoint.longitude_b ?? 0}]
            labels.push({text: cp.checkpoint?.name ?? '', pos: getMidpoint(position)})
            markers.push(position)
        })
        setCpsLabels(labels)
        setPolylinesCp(markers)

    }


    const onUnmount = React.useCallback(function callback(map: google.maps.Map) {
        setMap(null)
    }, [])


    const onLoad = React.useCallback(async (map: google.maps.Map) => {
        setFlagIcon({
            anchor: new google.maps.Point(32, 24),
            url: finishLine64,
            scaledSize: new google.maps.Size(50, 30),
        });

        // Set map type and control style only after map has loaded
        map.setMapTypeId(google.maps.MapTypeId.SATELLITE);

        // Set map type control options properly
        map.setOptions({
            mapTypeControl: true,
            mapTypeControlOptions: {
                style: google.maps.MapTypeControlStyle.HORIZONTAL_BAR
            }
        });
        setMap(map)

    }, [])


    //renders

    const handleHorsePos = () => {
        if (isEmpty(latLngs) || !map) {
            return <></>
        }

        let last_element = latLngs[latLngs.length - 1]

        delete last_element?.color


        return <Marker
            position={last_element}
            draggable={false}
            icon={{
                path: 'M5,10a5,5 0 1,0 10,0a5,5 0 1,0 -10,0',
                fillColor: '#99141A',
                fillOpacity: 1,
                scale: 1.1,
                strokeColor: '#FFFFFF',
                strokeWeight: 1,
                origin: new google.maps.Point(0, 0),
                anchor: new google.maps.Point(10, 10),
            }}

        />
    }

    // Function to generate polyline segments with different colors for each segment
    const getPolylineSegments = () => {

        if (isEmpty(latLngs) || !map) return <></>


        return latLngs.map((point, index) => {
            if (index === latLngs.length - 1) return null; // Skip the last point (no next point for polyline)

            const segmentPath = [latLngs[index], latLngs[index + 1]]; // Define the current segment (from one point to the next)

            return (
                <Polyline
                    key={index}
                    path={segmentPath}
                    options={{
                        strokeColor: point.color, // Color for the current segment
                        strokeOpacity: 1.0,
                        strokeWeight: 3,
                        geodesic: false,
                        zIndex: index + 1
                    }}
                />
            );
        });
    };

    return (<GoogleMap
        id='gMap'
        mapContainerStyle={{...containerStyle, height: isSmallScreen ? '250px' : '450px'}}
        onLoad={onLoad}
        onUnmount={onUnmount}
        center={center}

        zoom={16}
        options={{
            zoomControl: true,
            streetViewControl: false,
            maxZoom: 18,
            gestureHandling: 'cooperative',
            disableDefaultUI: true,
            keyboardShortcuts: true,
            mapTypeId: 'satellite',     // Force Satellite view
            mapTypeControl: false,      // Hide the dropdown map type control
        }}
    >

        { /* Child components, such as markers, info windows, etc. */}

        {/* horse dynamic pos */}
        {handleHorsePos()}
        {/* Draw polyline segments with different colors */}
        {getPolylineSegments()}

        {/* Finish Line line */}

        <Polyline
            path={polylineFl} // Connect marker positions
            options={{
                strokeColor: "#000", // Line color
                strokeOpacity: 0.8, // Line opacity
                strokeWeight: 2, // Line thickness
                zIndex: 1001
            }}
        />

        {/* Finish Line Flag */}
        {
            flagFl && flagIcon && <Marker
                position={flagFl}
                icon={flagIcon}
            />
        }

        {/* Checkpoints Polylines */}
        {polylinesCp.map((cp, index) => <Polyline
            key={index}
            path={cp} // Connect marker positions
            options={{
                strokeColor: "#000", // Line color
                strokeOpacity: 0.8, // Line opacity
                strokeWeight: 2, // Line thickness
                zIndex: 1001
            }}
        />)}

        {/* CheckPoints Labels */}
        {
            cpsLabels.map((label, index) =>
                <OverlayView key={`label-${index}`} position={label.pos} mapPaneName={OverlayView.FLOAT_PANE}>
                    <Typography fontWeight={500} whiteSpace={'nowrap'} color={'black'} variant='caption'>
                        {label.text}
                    </Typography>
                </OverlayView>)
        }
        <></>
    </GoogleMap>)
}

const containerStyle = {
    width: '100%',
    height: '500px',
    borderRadius: 10
};


MapV2.defaultProps = {
    center: {
        lat: -3.745,
        lng: -38.523
    },
    height: '250px',
}

export default MapV2

